/**
 * Vendor list + slide-out edit panel (18.5).
 */

import { useState } from 'react';
import useEntityList from '../../hooks/useEntityList';
import { fetchVendors, createVendor, updateVendor, deleteVendor, restoreVendor } from '../../api/vendors';
import { SlideOutPanel, Button, LoadingSpinner, EmptyState, Pagination } from '../common';
import PanelDocuments from './PanelDocuments';

export default function VendorList({ search, showDeleted }) {
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const { items, loading, totalCount, page, setPage, refresh } = useEntityList(
    fetchVendors,
    { search, showDeleted, extraParams: needsReviewOnly ? { needs_review: true } : {} },
  );
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  function openCreate() {
    setForm({
      vendor_name: '', vendor_code: '', vendor_type: 'standard',
      contact_email: '', phone: '', address_line1: '', address_line2: '',
      city: '', state: '', zip: '', website_url: '',
      contact_first_name: '', contact_last_name: '', contact_phone: '',
      preferred_comm_method: 'email',
      notes: '',
      uses_list_pricing: false, default_price_multiplier: '', quote_parsing_instructions: '',
      rfq_instructions: '',
      api_pricing_enabled: false, api_ordering_enabled: false,
    });
    setCreating(true);
    setSelected(null);
  }

  function openEdit(item) {
    setForm({
      vendor_name: item.vendor_name || '',
      vendor_code: item.vendor_code || '',
      vendor_type: item.vendor_type || 'standard',
      contact_email: item.contact_email || '',
      phone: item.phone || '',
      address_line1: item.address_line1 || '',
      address_line2: item.address_line2 || '',
      city: item.city || '',
      state: item.state || '',
      zip: item.zip || '',
      website_url: item.website_url || '',
      contact_first_name: item.contact_first_name || '',
      contact_last_name: item.contact_last_name || '',
      contact_phone: item.contact_phone || '',
      preferred_comm_method: item.preferred_comm_method || 'email',
      notes: item.notes || '',
      uses_list_pricing: !!item.uses_list_pricing,
      default_price_multiplier: item.default_price_multiplier ?? '',
      quote_parsing_instructions: item.quote_parsing_instructions || '',
      rfq_instructions: item.rfq_instructions || '',
      api_pricing_enabled: !!item.api_pricing_enabled,
      api_ordering_enabled: !!item.api_ordering_enabled,
    });
    setSelected(item);
    setCreating(false);
  }

  function closePanel() { setSelected(null); setCreating(false); }

  async function handleSave() {
    setSaving(true);
    try {
      if (creating) await createVendor(form);
      else await updateVendor(selected.id, form);
      closePanel();
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this vendor?')) return;
    try { await deleteVendor(selected.id); closePanel(); refresh(); }
    catch (err) { alert(err.response?.data?.error || 'Delete failed'); }
  }

  async function handleRestore() {
    try { await restoreVendor(selected.id); closePanel(); refresh(); }
    catch (err) { alert(err.response?.data?.error || 'Restore failed'); }
  }

  if (loading) return <div className="flex justify-center py-10"><LoadingSpinner /></div>;

  return (
    <div>
      <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-3">
        <Button size="sm" onClick={openCreate}>+ New Vendor</Button>
        <button
          onClick={() => setNeedsReviewOnly(!needsReviewOnly)}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
            needsReviewOnly
              ? 'bg-amber-100 border-amber-300 text-amber-800'
              : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${needsReviewOnly ? 'bg-amber-500' : 'bg-gray-400'}`} />
          Needs Review
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No vendors" description="Add vendors to start managing supplier relationships." />
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Phone</th>
                <th className="px-4 py-2 text-center">API</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => openEdit(item)}
                  className={`cursor-pointer ${
                    item.deleted_at ? 'opacity-50 line-through hover:bg-blue-50' :
                    item.needs_review ? 'bg-amber-50 hover:bg-amber-100' :
                    'hover:bg-blue-50'
                  }`}
                >
                  <td className="px-4 py-2 font-mono text-xs text-gray-600">{item.vendor_code || '—'}</td>
                  <td className="px-4 py-2 font-medium text-foreground">
                    <span>{item.vendor_name}</span>
                    {item.needs_review && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-200 text-amber-800 uppercase tracking-wide">Needs Info</span>
                    )}
                    {item.is_stock_vendor && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 uppercase tracking-wide">Stock</span>
                    )}

                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      item.vendor_type === 'online' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {item.vendor_type}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-500">{item.contact_email || '—'}</td>
                  <td className="px-4 py-2 text-gray-500">{item.phone || '—'}</td>
                  <td className="px-4 py-2 text-center">
                    {(item.api_pricing_enabled || item.api_ordering_enabled) && (
                      <span className="text-green-600 text-xs">Enabled</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2">
            <Pagination current={page} total={Math.ceil(totalCount / 200)} onChange={setPage} />
          </div>
        </>
      )}

      <SlideOutPanel
        open={!!selected || creating}
        onClose={closePanel}
        title={creating ? 'New Vendor' : `Edit: ${selected?.vendor_name || ''}`}
        width="max-w-lg"
      >
        <div className="space-y-4">
          {selected?.needs_review && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm text-amber-800">
              <span className="text-base leading-none mt-0.5">⚠</span>
              <span>
                {selected.vendor_type === 'online'
                  ? 'Add a website URL and save to clear this flag.'
                  : 'Add a contact email address and save to clear this flag.'}
              </span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <TextInput label="Vendor Code" value={form.vendor_code} onChange={(v) => setForm({ ...form, vendor_code: v })} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={form.vendor_type || 'standard'}
                onChange={(e) => setForm({ ...form, vendor_type: e.target.value })}
              >
                <option value="standard">Standard</option>
                <option value="online">Online</option>
              </select>
            </div>
          </div>
          <TextInput label="Vendor Name" value={form.vendor_name} onChange={(v) => setForm({ ...form, vendor_name: v })} />
          <TextInput label="Website" value={form.website_url} onChange={(v) => setForm({ ...form, website_url: v })} />

          {/* Address */}
          <TextInput label="Address Line 1" value={form.address_line1} onChange={(v) => setForm({ ...form, address_line1: v })} />
          <TextInput label="Address Line 2" value={form.address_line2} onChange={(v) => setForm({ ...form, address_line2: v })} />
          <div className="grid grid-cols-3 gap-3">
            <TextInput label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
            <TextInput label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
            <TextInput label="Zip" value={form.zip} onChange={(v) => setForm({ ...form, zip: v })} />
          </div>
          <TextInput label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />

          {/* Contact */}
          <h4 className="text-xs font-semibold text-gray-500 uppercase pt-2">Contact Person</h4>
          <div className="grid grid-cols-2 gap-3">
            <TextInput label="First Name" value={form.contact_first_name} onChange={(v) => setForm({ ...form, contact_first_name: v })} />
            <TextInput label="Last Name" value={form.contact_last_name} onChange={(v) => setForm({ ...form, contact_last_name: v })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextInput label="Email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} type="email" />
            <TextInput label="Contact Phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Comm</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.preferred_comm_method || 'email'}
              onChange={(e) => setForm({ ...form, preferred_comm_method: e.target.value })}
            >
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="fax">Fax</option>
            </select>
          </div>

          <TextInput label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} multiline />

          {/* List Pricing */}
          <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">List Pricing</h4>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.uses_list_pricing || false}
                onChange={(e) => setForm({ ...form, uses_list_pricing: e.target.checked })}
                className="rounded border-gray-300 text-blue-600"
              />
              <span className="font-medium">This vendor quotes list prices</span>
            </label>
            {form.uses_list_pricing && (
              <div className="mt-2 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Multiplier</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      className="w-24 border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="0.80"
                      value={form.default_price_multiplier}
                      onChange={(e) => setForm({ ...form, default_price_multiplier: e.target.value })}
                    />
                    <span className="text-xs text-gray-400">e.g. 0.80 = 20% off list</span>
                  </div>
                  {form.default_price_multiplier && (
                    <p className="text-xs text-gray-500 mt-1">
                      List $100 × {form.default_price_multiplier} = Your cost ${(100 * parseFloat(form.default_price_multiplier || 0)).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* RFQ Instructions — included in outbound RFQ emails */}
          <div className="border border-gray-200 rounded-md p-3 bg-blue-50">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Outbound RFQ Email</h4>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              RFQ Instructions
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              rows={2}
              placeholder='e.g. "Please provide NET pricing" or "Include freight to Gray, LA 70359"'
              value={form.rfq_instructions}
              onChange={(e) => setForm({ ...form, rfq_instructions: e.target.value })}
            />
            <p className="text-xs text-gray-400 mt-1">
              Special instructions included in RFQ emails sent to this vendor.
            </p>
          </div>

          {/* AI Quote Parsing Instructions — available for ALL vendors */}
          <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">AI Quote Parsing</h4>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quote Parsing Instructions
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
              rows={6}
              placeholder="Instructions for the AI when parsing quotes and order acknowledgements from this vendor..."
              value={form.quote_parsing_instructions}
              onChange={(e) => setForm({ ...form, quote_parsing_instructions: e.target.value })}
            />
            <p className="text-xs text-gray-400 mt-1">
              These instructions are injected into the AI prompt when processing emails from this vendor.
              Describe how this vendor formats quotes, what columns mean, and any special calculations.
            </p>
          </div>

          {/* API Config (only for online vendors) */}
          {form.vendor_type === 'online' && (
            <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">API Configuration</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.api_pricing_enabled || false}
                    onChange={(e) => setForm({ ...form, api_pricing_enabled: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600"
                  />
                  Pricing API enabled
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.api_ordering_enabled || false}
                    onChange={(e) => setForm({ ...form, api_ordering_enabled: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600"
                  />
                  Ordering API enabled
                </label>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
            <Button loading={saving} onClick={handleSave}>Save</Button>
            <Button variant="ghost" onClick={closePanel}>Cancel</Button>
            {selected && !selected.deleted_at && (
              <Button variant="danger" size="sm" className="ml-auto" onClick={handleDelete}>Delete</Button>
            )}
            {selected?.deleted_at && (
              <Button variant="secondary" size="sm" className="ml-auto" onClick={handleRestore}>Restore</Button>
            )}
          </div>

          {selected && <PanelDocuments entityType="vendor" entityId={selected.id} />}
        </div>
      </SlideOutPanel>
    </div>
  );
}

function TextInput({ label, value, onChange, type = 'text', multiline = false }) {
  const cls = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {multiline ? (
        <textarea className={cls} rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} className={cls} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}
